import React, { useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material';
import { MarketList } from '@market/react';
import { useTranslation } from 'react-i18next';
import ProductActionCard from 'components/pages/product/product_action_card';
import {
  getAlternativeUrl,
  TRACKING_EVENT_TYPES,
} from 'components/pages/product/helpers';
import {
  getKioskMountConfigurations,
  sortProductAddOnSections,
  withCanonicalPresentation,
} from 'components/pages/product/product_with_software_add_on/utils';
import { displayVAT, toCurrency } from 'utilities/currency';
import { trackCdp, trackEvent } from 'utilities/tracking';
import { useProductContext } from 'typescript/pages/product/contexts/product_context';

const KIOSK_MOUNT_SUBTEXT_KEYS = Object.freeze({
  'k1-countertop-stand':
    'relations.alternative.category.kiosk_mount.countertopHeightWarning',
});

const mountForConfiguration = (configuration, canonicalProduct) =>
  configuration.parts_included?.find((part) =>
    part.product_key
      ? part.product_key !== canonicalProduct.product_key
      : part.base_product_key !== canonicalProduct.base_product_key,
  );

const KioskMountSelector = ({ alternatives, canonicalAltMetadata = {} }) => {
  const {
    product: canonicalProduct,
    currentProduct,
    setCurrentProduct,
    setSelection,
    addOns,
    isGetAddOnsLoading,
    cart,
  } = useProductContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const softwareInitializedRef = useRef(false);

  const configurations = useMemo(
    () =>
      getKioskMountConfigurations(
        alternatives,
        canonicalProduct,
        canonicalAltMetadata,
      ),
    [alternatives, canonicalAltMetadata, canonicalProduct],
  );

  const defaultConfiguration = configurations.find(
    (configuration) =>
      configuration.product_key !== canonicalProduct.product_key,
  );

  useEffect(() => {
    if (isGetAddOnsLoading) return;

    const { softwareAddOnSections } = sortProductAddOnSections(addOns);
    const shouldPreselectSoftware =
      !softwareInitializedRef.current && softwareAddOnSections.length > 0;
    const preselectedSoftware = shouldPreselectSoftware
      ? softwareAddOnSections.reduce(
          (selected, { category, addOns: categoryAddOns }) => {
            if (categoryAddOns.length === 0) {
              return selected;
            }

            return {
              ...selected,
              [category]: categoryAddOns[0].variant_id.toString(),
            };
          },
          {},
        )
      : {};

    if (shouldPreselectSoftware) {
      softwareInitializedRef.current = true;
    }

    setSelection((previousSelection) => {
      const validPreviousAddOns = Object.entries(
        previousSelection.addOns,
      ).reduce((validAddOns, [category, variantId]) => {
        const selectionStillExists = addOns[category]?.some(
          (addOn) => addOn.variant_id.toString() === variantId.toString(),
        );

        return selectionStillExists
          ? { ...validAddOns, [category]: variantId }
          : validAddOns;
      }, {});

      return {
        ...previousSelection,
        addOns: { ...preselectedSoftware, ...validPreviousAddOns },
      };
    });
  }, [addOns, isGetAddOnsLoading, setSelection]);

  if (!defaultConfiguration) return null;

  const mountLabel = (configuration) =>
    mountForConfiguration(configuration, canonicalProduct)?.name ||
    t('relations.alternative.category.kiosk_mount.bringOwnLabel');

  const mountSubtext = (configuration) => {
    const mount = mountForConfiguration(configuration, canonicalProduct);

    const subtextKey =
      KIOSK_MOUNT_SUBTEXT_KEYS[mount?.base_product_key] ||
      KIOSK_MOUNT_SUBTEXT_KEYS[mount?.product_key];

    return subtextKey ? t(subtextKey) : undefined;
  };

  const priceLabel = (configuration) => {
    const difference =
      configuration.sale_price_unformatted -
      defaultConfiguration.sale_price_unformatted;

    if (difference === 0) {
      return t('relations.alternative.category.kiosk_mount.included');
    }

    const label = `${difference > 0 ? '+' : '-'}${toCurrency(
      Math.abs(difference),
    )}`;

    return displayVAT() ? (
      <>
        {`${label} `}
        <span style={{ fontSize: theme.type.fontSize.tiny }}>
          {t('common.plusVAT')}
        </span>
      </>
    ) : (
      label
    );
  };

  const onConfigurationSelected = (productKey) => {
    if (productKey === null) return;

    const selectedConfiguration = configurations.find(
      (configuration) => configuration.product_key === productKey,
    );
    if (!selectedConfiguration) return;

    const alternativeUrl = getAlternativeUrl(canonicalProduct.slug);
    const choice = mountLabel(selectedConfiguration);

    trackEvent({
      name: 'Alternative',
      value: 'Change',
      params: {
        choice,
        alternativeUrl,
        cartNumber: cart?.number,
        ...selectedConfiguration.tracking_data,
      },
    });
    trackCdp({
      name: TRACKING_EVENT_TYPES.ChangeAlternative,
      params: {
        feature: 'shop',
        choice,
        alternativeUrl,
        cartNumber: cart?.number,
        ...selectedConfiguration.tracking_data,
      },
    });

    setCurrentProduct(
      withCanonicalPresentation(selectedConfiguration, canonicalProduct),
    );

    setSelection((previousSelection) => {
      const maximumQuantity = selectedConfiguration.max_quantity;
      if (!Number.isFinite(maximumQuantity)) return previousSelection;

      return {
        ...previousSelection,
        quantity: Math.max(
          1,
          Math.min(previousSelection.quantity, maximumQuantity),
        ),
      };
    });
  };

  return (
    <div>
      <p
        className="kiosk-section-title"
        style={{ margin: theme.spacing(2, 0) }}
      >
        {t('relations.alternative.category.kiosk_mount.title')}
      </p>
      <MarketList
        interactive
        value={currentProduct.product_key}
        onMarketListSelectionsDidChange={(event) =>
          onConfigurationSelected(event.detail.newSelectionValue)
        }
        style={{ margin: theme.spacing(2, 0) }}
      >
        {configurations.map((configuration) => (
          <ProductActionCard
            key={configuration.product_key}
            value={configuration.product_key}
            label={mountLabel(configuration)}
            subtext={mountSubtext(configuration)}
            secondaryLabel={priceLabel(configuration)}
          />
        ))}
      </MarketList>
    </div>
  );
};

export default KioskMountSelector;
